import { useCallback, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import type { AddChecklistItemInputProps } from '../type'
import {
  useChecklistItemMutations,
  useChecklistItems,
} from '@/lib/db/rxdb/hooks'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { cn } from '@/lib/utils'

export function AddChecklistItemInput({
  listId,
  className,
}: AddChecklistItemInputProps) {
  const [content, setContent] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const isSubmittingRef = useRef(false)
  const { checklistItems: items } = useChecklistItems({
    checklistId: listId,
  })
  const { createChecklistItem } = useChecklistItemMutations()

  const handleAddItem = useCallback(async () => {
    if (isSubmittingRef.current) return
    const trimmed = content.trim()

    if (trimmed.length === 0) {
      setContent('')
      setIsExpanded(false)
      return
    }

    isSubmittingRef.current = true

    // Calculate next sort order - append after the largest rank, since the
    // hook's query is unsorted and `items[length-1]` may not be the max.
    const maxSortOrder = items.reduce<string | null>(
      (max, it) =>
        it.sortOrder && (!max || compareRanks(it.sortOrder, max) > 0)
          ? it.sortOrder
          : max,
      null,
    )
    const nextSortOrder = generateRankBetween(maxSortOrder, null)

    try {
      await createChecklistItem({
        checklistId: listId,
        content: trimmed,
        isCompleted: false,
        sortOrder: nextSortOrder,
      })
      setContent('')

      // Keep input focused for adding more items
      if (inputRef.current) {
        inputRef.current.focus()
      }
    } catch (error) {
      // Error handling
    } finally {
      isSubmittingRef.current = false
    }
  }, [content, listId, items, createChecklistItem])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void handleAddItem()
      } else if (e.key === 'Escape') {
        setContent('')
        setIsExpanded(false)
        inputRef.current?.blur()
      }
    },
    [handleAddItem],
  )

  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => {
          setIsExpanded(true)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition hover:bg-accent hover:text-foreground',
          className,
        )}
      >
        <div className="size-4" /> {/* Spacer for checkbox alignment */}
        <Plus className="size-4" />
        <span>Add an item</span>
      </button>
    )
  }

  return (
    <div className={cn('flex items-center gap-2 px-2', className)}>
      <div className="size-4" /> {/* Spacer for checkbox alignment */}
      <input
        ref={inputRef}
        type="text"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onBlur={() => {
          if (content.trim().length === 0) {
            setIsExpanded(false)
          } else {
            void handleAddItem()
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder="Add an item..."
        className="flex-1 rounded border border-border bg-background px-2 py-1 text-base text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary md:text-sm"
      />
    </div>
  )
}
